import React, {

  useCallback,

  useContext,

  useEffect,

  useMemo,

  useRef,

  useState,

} from "react";

import { FaEdit, FaTrash } from "react-icons/fa";

import { Menu, PackageSearch, X } from "lucide-react";

import { ProductsContext } from "../../context/ProductsContext";

import AddProductForm from "./AddProductForm";

import Sidebar from "../../components/Sidebar";

import { API_BASE_URL } from "../../config/api";

const ITEMS_PER_PAGE = 10;

const EMPTY_MESSAGE = {

  type: "",

  text: "",

};

const AdminProducts = () => {

  const {

    products = [],

    fetchProducts,

    setProducts,

  } = useContext(ProductsContext);

  const dialogRef = useRef(null);

  const formRef = useRef(null);

  const [editingProduct, setEditingProduct] = useState(null);

  const [isEditMode, setIsEditMode] = useState(false);

  const [search, setSearch] = useState("");

  const [category, setCategory] = useState("All");

  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [saving, setSaving] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);

  const [message, setMessage] = useState(EMPTY_MESSAGE);

  const [modalMessage, setModalMessage] =

    useState(EMPTY_MESSAGE);

  // =========================================================

  // LOAD PRODUCTS

  // =========================================================

  useEffect(() => {

    fetchProducts();

  }, [fetchProducts]);

  // =========================================================

  // RESET PAGINATION WHEN FILTERS CHANGE

  // =========================================================

  useEffect(() => {

    setCurrentPage(1);

  }, [search, category]);

  // =========================================================

  // CATEGORIES

  // =========================================================

  const categories = useMemo(() => {

    return [

      ...new Set(

        products

          .map((product) => product.category)

          .filter(Boolean)

      ),

    ].sort((a, b) =>

      String(a).localeCompare(String(b))

    );

  }, [products]);

  // =========================================================

  // FILTER PRODUCTS

  // =========================================================

  const filteredProducts = useMemo(() => {

    const query = search.trim().toLowerCase();

    return products.filter((product) => {

      const matchesSearch =

        !query ||

        String(product.id ?? "")

          .toLowerCase()

          .includes(query) ||

        String(product.name ?? "")

          .toLowerCase()

          .includes(query) ||

        String(product.category ?? "")

          .toLowerCase()

          .includes(query);

      const matchesCategory =

        category === "All" ||

        product.category === category;

      return matchesSearch && matchesCategory;

    });

  }, [products, search, category]);

  // =========================================================

  // PAGINATION

  // =========================================================

  const totalPages = Math.max(

    1,

    Math.ceil(

      filteredProducts.length / ITEMS_PER_PAGE

    )

  );

  const safePage = Math.min(

    currentPage,

    totalPages

  );

  const displayedProducts = useMemo(() => {

    const startIndex =

      (safePage - 1) * ITEMS_PER_PAGE;

    return filteredProducts.slice(

      startIndex,

      startIndex + ITEMS_PER_PAGE

    );

  }, [filteredProducts, safePage]);

  // =========================================================

  // MODAL HELPERS

  // =========================================================

  const resetModalState = useCallback(() => {

    formRef.current?.resetForm();

    setEditingProduct(null);

    setIsEditMode(false);

    setModalMessage(EMPTY_MESSAGE);

  }, []);

  const closeDialog = useCallback(() => {

    if (saving) return;

    dialogRef.current?.close();

    resetModalState();

  }, [saving, resetModalState]);

  // =========================================================

  // OPEN ADD MODAL

  // =========================================================

  const openAddDialog = () => {

    setEditingProduct(null);

    setIsEditMode(true);

    setModalMessage(EMPTY_MESSAGE);

    formRef.current?.resetForm();

    dialogRef.current?.showModal();

  };

  // =========================================================

  // OPEN PRODUCT VIEW

  // =========================================================

  const openProductDialog = (product) => {

    setEditingProduct(product);

    setIsEditMode(false);

    setModalMessage(EMPTY_MESSAGE);

    window.setTimeout(() => {

      dialogRef.current?.showModal();

    }, 0);

  };

  // =========================================================

  // ENABLE EDIT MODE

  // =========================================================

  const enableEditMode = () => {

    setModalMessage(EMPTY_MESSAGE);

    setIsEditMode(true);

  };

  // =========================================================

  // SAVE PRODUCT

  // =========================================================

  const handleSaveProduct = async (productFormData) => {

    const token = localStorage.getItem("authToken");

    if (!token) {

      const error = new Error(

        "Sesioni ka përfunduar. Hyr përsëri në sistem."

      );

      setModalMessage({

        type: "error",

        text: error.message,

      });

      throw error;

    }

    const isEditing = Boolean(

      editingProduct?.id

    );

    const url = isEditing

      ? `${API_BASE_URL}/api/products/${editingProduct.id}`

      : `${API_BASE_URL}/api/products`;

    setSaving(true);

    setModalMessage(EMPTY_MESSAGE);

    try {

      const response = await fetch(url, {

        method: isEditing ? "PUT" : "POST",

        headers: {

          Authorization: `Bearer ${token}`,

        },

        body: productFormData,

      });

      const responseText =

        await response.text();

      let data = {};

      try {

        data = responseText

          ? JSON.parse(responseText)

          : {};

      } catch {

        data = {

          error:

            responseText ||

            "Përgjigje e pavlefshme nga serveri.",

        };

      }

      if (!response.ok) {

        throw new Error(

          data?.details ||

            data?.sqlMessage ||

            data?.error ||

            data?.message ||

            `Ruajtja dështoi (${response.status})`

        );

      }

      await fetchProducts();

      dialogRef.current?.close();

      formRef.current?.resetForm();

      setEditingProduct(null);

      setIsEditMode(false);

      setModalMessage(EMPTY_MESSAGE);

      setMessage({

        type: "success",

        text: isEditing

          ? "Produkti u përditësua me sukses."

          : "Produkti u shtua me sukses.",

      });

      return data;

    } catch (error) {

      console.error(

        "PRODUCT SAVE ERROR:",

        error

      );

      setModalMessage({

        type: "error",

        text:

          error.message ||

          "Produkti nuk mund të ruhej.",

      });

      throw error;

    } finally {

      setSaving(false);

    }

  };

  // =========================================================

  // DELETE PRODUCT

  // =========================================================

  const handleRemove = async (

    id,

    closeModalAfterDelete = false

  ) => {

    const product = products.find(

      (item) =>

        Number(item.id) === Number(id)

    );

    const confirmed = window.confirm(

      `A je e sigurt që do ta fshish produktin "${product?.name || id}"?`

    );

    if (!confirmed) return;

    const token =

      localStorage.getItem("authToken");

    if (!token) {

      const errorMessage =

        "Sesioni ka përfunduar. Hyr përsëri në sistem.";

      if (closeModalAfterDelete) {

        setModalMessage({

          type: "error",

          text: errorMessage,

        });

      } else {

        setMessage({

          type: "error",

          text: errorMessage,

        });

      }

      return;

    }

    try {

      const response = await fetch(

        `${API_BASE_URL}/api/products/${id}`,

        {

          method: "DELETE",

          headers: {

            Authorization: `Bearer ${token}`,

          },

        }

      );

      const data =

        await response.json().catch(() => ({}));

      if (!response.ok) {

        throw new Error(

          data?.details ||

            data?.sqlMessage ||

            data?.error ||

            data?.message ||

            "Produkti nuk mund të fshihej."

        );

      }

      // Optimistic local update

      setProducts((previousProducts) =>

        previousProducts.filter(

          (item) =>

            Number(item.id) !== Number(id)

        )

      );

      if (closeModalAfterDelete) {

        dialogRef.current?.close();

        setEditingProduct(null);

        setIsEditMode(false);

        setModalMessage(EMPTY_MESSAGE);

      }

      setMessage({

        type: "success",

        text: "Produkti u fshi me sukses.",

      });

      await fetchProducts();

    } catch (error) {

      console.error(

        "DELETE PRODUCT ERROR:",

        error

      );

      const errorText =

        error.message ||

        "Produkti nuk mund të fshihej.";

      if (closeModalAfterDelete) {

        setModalMessage({

          type: "error",

          text: errorText,

        });

      } else {

        setMessage({

          type: "error",

          text: errorText,

        });

      }

    }

  };

  // =========================================================

  // CLEAR FILTERS

  // =========================================================

  const clearFilters = () => {

    setSearch("");

    setCategory("All");

  };

  // =========================================================

  // RENDER

  // =========================================================

  return (

    <div className="mp-products min-h-screen w-full overflow-x-hidden bg-gray-100 text-gray-800">

      <style>{productsDesign}</style>

      {/* SIDEBAR */}

      <Sidebar

        isOpen={sidebarOpen}

        onClose={() =>

          setSidebarOpen(false)

        }

      />

      {/* MOBILE MENU */}

      <button

        type="button"

        onClick={() =>

          setSidebarOpen(true)

        }

        className="fixed left-4 top-4 z-30 flex h-11 w-11 items-center justify-center rounded-xl bg-blue-700 text-white shadow-lg transition hover:bg-blue-800 active:scale-95 lg:hidden"

        aria-label="Hap menunë"

      >

        <Menu size={24} />

      </button>

      {/* MAIN */}

      <main className="min-h-screen w-full min-w-0 px-3 pb-6 pt-20 sm:px-5 lg:ml-64 lg:w-[calc(100%-16rem)] lg:px-6 lg:pt-6">

        {/* =====================================================

            HEADER

        ===================================================== */}

        <div className="mp-title mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">

          <div>

            <span className="text-xs font-bold uppercase tracking-wider text-blue-600">

              Produktet për Webin 

            </span>

            <h1 className="mt-1 text-3xl font-bold text-gray-900">

              Produktet

            </h1>

            <p className="mt-1 text-sm text-gray-500">

              Kliko mbi emrin e produktit për

              të parë të dhënat.

            </p>

          </div>

          <button

            type="button"

            onClick={openAddDialog}

            className="w-full rounded-xl bg-blue-600 px-5 py-3 font-semibold text-white shadow-md transition hover:bg-blue-700 active:scale-[0.98] sm:w-auto"

          >

            + Shto produkt

          </button>

        </div>

        {/* =====================================================

            GLOBAL MESSAGE

        ===================================================== */}

        {message.text && (

          <div

            className={`mb-4 rounded-xl border px-4 py-3 text-sm font-medium ${

              message.type === "success"

                ? "border-green-200 bg-green-50 text-green-700"

                : "border-red-200 bg-red-50 text-red-700"

            }`}

            role="alert"

          >

            {message.text}

          </div>

        )}

        {/* =====================================================

            FILTERS

        ===================================================== */}

        <section className="mp-stats" aria-label="Përmbledhja e produkteve">

{[

['Produkte gjithsej', products.length, 'Në katalog', 'blue'],

['Të publikuara', products.filter(p => Number(p.is_published || 0) === 1).length, 'Të dukshme në web', 'purple'],

['Kategori', categories.length, 'Kategori produktesh', 'green'],

['Të papublikuara', products.filter(p => Number(p.is_published || 0) !== 1).length, 'Të fshehura nga webi', 'red']

].map(([label,value,sub,tone]) => <article key={label} className={`mp-stat mp-${tone}`}><p>{label}</p><strong>{value}</strong><small>{sub}</small></article>)}

</section>

<section className="mp-filters mb-4 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm"><div className="mp-filter-heading"><div><h2>Lista e të gjitha produkteve</h2><p>Kërko dhe filtro produktet e katalogut.</p></div><span>{filteredProducts.length} produkte</span></div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_260px_auto]">

            {/* SEARCH */}

            <div>

              <label

                htmlFor="product-search"

                className="sr-only"

              >

                Kërko produkt

              </label>

              <input

                id="product-search"

                type="text"

                value={search}

                onChange={(event) =>

                  setSearch(event.target.value)

                }

                placeholder="Kërko sipas ID, emrit ose kategorisë..."

                className="w-full rounded-xl border border-gray-300 px-4 py-2.5 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"

              />

            </div>

            {/* CATEGORY */}

            <div>

              <label

                htmlFor="product-category"

                className="sr-only"

              >

                Zgjidh kategorinë

              </label>

              <select

                id="product-category"

                value={category}

                onChange={(event) =>

                  setCategory(event.target.value)

                }

                className="w-full rounded-xl border border-gray-300 bg-white px-4 py-2.5 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"

              >

                <option value="All">

                  Të gjitha kategoritë

                </option>

                {categories.map((item) => (

                  <option

                    key={item}

                    value={item}

                  >

                    {item}

                  </option>

                ))}

              </select>

            </div>

            {/* CLEAR */}

            <button

              type="button"

              onClick={clearFilters}

              className="rounded-xl bg-gray-100 px-4 py-2.5 font-semibold text-gray-700 transition hover:bg-gray-200"

            >

              Pastro

            </button>

          </div>

        </section>

        {/* =====================================================

            RESULTS COUNT

        ===================================================== */}

        <div className="mp-results mb-3 rounded-xl border border-blue-100 bg-blue-50 px-4 py-2.5 text-sm text-blue-700">

          <strong>

            {filteredProducts.length}

          </strong>{" "}

          produkte të gjetura

        </div>

        {/* =====================================================

            PRODUCTS TABLE

        ===================================================== */}

        <section className="mp-table-panel hidden md:block overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">

          <div className="overflow-x-auto">

            <table className="w-full min-w-[720px] text-sm">

              <thead className="bg-blue-600 text-white">

                <tr>

                  <th className="w-[90px] px-4 py-3 text-left">

                    ID

                  </th>

                  <th className="px-4 py-3 text-left">

                    Emri i produktit

                  </th>

                  <th className="w-[220px] px-4 py-3 text-left">

                    Kategoria

                  </th>

                  <th className="w-[150px] px-4 py-3 text-center">

                    Publikuar

                  </th>

                  <th className="w-[150px] px-4 py-3 text-center">

                    Veprime

                  </th>

                </tr>

              </thead>

              <tbody>

                {displayedProducts.length > 0 ? (

                  displayedProducts.map(

                    (product) => {

                      const isPublished =

                        Number(

                          product.is_published || 0

                        ) === 1;

                      return (

                        <tr

                          key={product.id}

                          tabIndex={0}

                          onClick={(event) => {

                            if (event.target.closest("button, a, input, select, textarea")) return;

                            openProductDialog(product);

                          }}

                          onKeyDown={(event) => {

                            if (event.target !== event.currentTarget) return;

                            if (event.key === "Enter" || event.key === " ") {

                              event.preventDefault();

                              openProductDialog(product);

                            }

                          }}

                          className="cursor-pointer border-b border-gray-100 transition-colors last:border-b-0 hover:bg-blue-50/50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500"

                        >

                          {/* ID */}

                          <td className="px-4 py-3 font-semibold text-gray-500">

                            #{product.id}

                          </td>

                          {/* NAME */}

                          <td className="px-4 py-3">

                            <button

                              type="button"

                              onClick={() =>

                                openProductDialog(

                                  product

                                )

                              }

                              className="max-w-full text-left font-bold text-blue-700 underline decoration-blue-200 underline-offset-4 transition hover:text-blue-900 hover:decoration-blue-600"

                            >

                              {product.name ||

                                "Pa emër"}

                            </button>

                          </td>

                          {/* CATEGORY */}

                          <td className="px-4 py-3 text-gray-600">

                            {product.category ||

                              "—"}

                          </td>

                          {/* PUBLISHED */}

                          <td className="px-4 py-3 text-center">

                            <span

                              className={`inline-flex min-w-[92px] justify-center rounded-full px-3 py-1 text-xs font-bold ${

                                isPublished

                                  ? "bg-green-100 text-green-700"

                                  : "bg-gray-100 text-gray-500"

                              }`}

                            >

                              {isPublished

                                ? "PO"

                                : "JO"}

                            </span>

                          </td>

                          {/* ACTIONS */}

                          <td className="px-4 py-3">

                            <div className="flex items-center justify-center gap-2">

                              <button

                                type="button"

                                onClick={() =>

                                  openProductDialog(

                                    product

                                  )

                                }

                                className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-50 text-blue-600 transition hover:bg-blue-100 active:scale-95"

                                aria-label={`Shiko ${product.name}`}

                                title="Shiko produktin"

                              >

                                <FaEdit />

                              </button>

                              <button

                                type="button"

                                onClick={() =>

                                  handleRemove(

                                    product.id

                                  )

                                }

                                className="flex h-9 w-9 items-center justify-center rounded-lg bg-red-50 text-red-600 transition hover:bg-red-100 active:scale-95"

                                aria-label={`Fshi ${product.name}`}

                                title="Fshi produktin"

                              >

                                <FaTrash />

                              </button>

                            </div>

                          </td>

                        </tr>

                      );

                    }

                  )

                ) : (

                  <tr>

                    <td

                      colSpan="5"

                      className="px-4 py-16 text-center text-gray-500"

                    >

                      <PackageSearch

                        size={42}

                        className="mx-auto mb-3 text-gray-300"

                      />

                      <p className="font-semibold">

                        Nuk u gjet asnjë produkt.

                      </p>

                      {(search ||

                        category !== "All") && (

                        <button

                          type="button"

                          onClick={

                            clearFilters

                          }

                          className="mt-3 text-sm font-semibold text-blue-600 hover:text-blue-800"

                        >

                          Pastro filtrat

                        </button>

                      )}

                    </td>

                  </tr>

                )}

              </tbody>

            </table>

          </div>

        </section>

        {/* PRODUKTET — KARTA PËR MOBILE */}
        <section className="space-y-3 md:hidden" aria-label="Lista e produkteve">
          {displayedProducts.length > 0 ? displayedProducts.map((product) => {
            const isPublished = Number(product.is_published || 0) === 1;
            return (
              <article
                key={product.id}
                className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm"
              >
                <button
                  type="button"
                  onClick={() => openProductDialog(product)}
                  className="block w-full p-4 text-left transition hover:bg-gray-50 active:bg-blue-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500"
                  aria-label={`Shiko detajet: ${product.name || "Pa emër"}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <span className="text-xs font-medium text-gray-500">#{product.id}</span>
                    <span className={`shrink-0 rounded-full px-3 py-1 text-xs font-semibold ${isPublished ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                      Publikuar: {isPublished ? "PO" : "JO"}
                    </span>
                  </div>
                  <h3 className="mt-3 break-words text-base font-semibold leading-6 text-gray-900">
                    {product.name || "Pa emër"}
                  </h3>
                  <div className="mt-3 flex items-start gap-2 text-sm text-gray-600">
                    <PackageSearch size={17} className="mt-0.5 shrink-0 text-gray-400" />
                    <span className="break-words">{product.category || "—"}</span>
                  </div>
                  <span className="mt-4 inline-flex text-sm font-semibold text-blue-600">
                    Shiko detajet →
                  </span>
                </button>
                <div className="flex items-center justify-end gap-2 border-t border-gray-100 px-4 py-3">
                  <button
                    type="button"
                    onClick={() => openProductDialog(product)}
                    className="flex h-9 items-center justify-center gap-2 rounded-lg border border-blue-100 bg-blue-50 px-3 text-sm font-medium text-blue-600 hover:bg-blue-100"
                    aria-label={`Shiko ${product.name || "produktin"}`}
                  >
                    <FaEdit /> Shiko
                  </button>
                  <button
                    type="button"
                    onClick={() => handleRemove(product.id)}
                    className="flex h-9 items-center justify-center gap-2 rounded-lg border border-red-100 bg-red-50 px-3 text-sm font-medium text-red-600 hover:bg-red-100"
                    aria-label={`Fshi ${product.name || "produktin"}`}
                  >
                    <FaTrash /> Fshi
                  </button>
                </div>
              </article>
            );
          }) : (
            <div className="rounded-2xl border border-gray-200 bg-white px-4 py-12 text-center text-sm text-gray-500">
              <PackageSearch size={42} className="mx-auto mb-3 text-gray-300" />
              <p className="font-semibold">Nuk u gjet asnjë produkt.</p>
              {(search || category !== "All") && (
                <button type="button" onClick={clearFilters} className="mt-3 font-semibold text-blue-600 hover:text-blue-800">
                  Pastro filtrat
                </button>
              )}
            </div>
          )}
        </section>

        {/* =====================================================

            PAGINATION

        ===================================================== */}

        <div className="mp-pagination mt-4 flex items-center justify-center gap-3">

          <button

            type="button"

            disabled={safePage === 1}

            onClick={() =>

              setCurrentPage((page) =>

                Math.max(1, page - 1)

              )

            }

            className="h-10 rounded-lg border border-gray-300 bg-white px-4 font-medium text-gray-700 shadow-sm transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"

            aria-label="Faqja e mëparshme"

          >

            ←

          </button>

          <span className="min-w-[90px] rounded-lg bg-white px-3 py-2 text-center text-sm font-semibold text-gray-700 shadow-sm">

            {safePage} / {totalPages}

          </span>

          <button

            type="button"

            disabled={

              safePage === totalPages

            }

            onClick={() =>

              setCurrentPage((page) =>

                Math.min(

                  totalPages,

                  page + 1

                )

              )

            }

            className="h-10 rounded-lg border border-gray-300 bg-white px-4 font-medium text-gray-700 shadow-sm transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"

            aria-label="Faqja tjetër"

          >

            →

          </button>

        </div>

   {/* =====================================================

    PRODUCT MODAL

===================================================== */}

<dialog

  ref={dialogRef}

  onCancel={(event) => {

    event.preventDefault();

    closeDialog();

  }}

  className="mp-product-dialog m-auto w-[calc(100%-1rem)] max-w-4xl overflow-hidden rounded-2xl bg-white p-0 shadow-2xl backdrop:bg-slate-900/60 backdrop:backdrop-blur-sm"

>

  {/* ===================================================

      MODAL HEADER

  =================================================== */}

  <div className="border-b border-gray-200 bg-white">

    <div className="flex items-start justify-between gap-4 px-6 py-5">

      <div className="min-w-0">

        <div className="mb-1 flex items-center gap-2">

          <span className="text-xs font-bold uppercase tracking-wider text-blue-600">

            {editingProduct

              ? `Produkt #${editingProduct.id}`

              : "Produkt i ri"}

          </span>

          {editingProduct && (

            <span

              className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${

                Number(

                  editingProduct.is_published || 0

                ) === 1

                  ? "bg-green-100 text-green-700"

                  : "bg-gray-100 text-gray-500"

              }`}

            >

              {Number(

                editingProduct.is_published || 0

              ) === 1

                ? "PUBLIKUAR"

                : "JO PUBLIKUAR"}

            </span>

          )}

        </div>

        <h2 className="truncate text-xl font-bold text-gray-900 sm:text-2xl">

          {editingProduct?.name ||

            "Shto produkt të ri"}

        </h2>

        <p className="mt-1 text-sm text-gray-500">

          {isEditMode

            ? "Modifiko të dhënat e produktit."

            : "Shiko informacionet e produktit."}

        </p>

      </div>

      <button

        type="button"

        onClick={closeDialog}

        disabled={saving}

        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-500 transition hover:border-red-200 hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-50"

        aria-label="Mbyll modalin"

      >

        <X size={20} />

      </button>

    </div>

    {/* =================================================

        TOP ACTION BAR

    ================================================= */}

    {editingProduct && (

      <div className="flex flex-col gap-3 border-t border-gray-100 bg-gray-50/80 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">

        <div>

          <p className="text-sm font-semibold text-gray-700">

            Veprimet e produktit

          </p>

          <p className="text-xs text-gray-500">

            Menaxho produktin nga këtu.

          </p>

        </div>

        <div className="flex flex-col gap-2 sm:flex-row">

          {/* EDIT */}

          <button

            type="button"

            onClick={enableEditMode}

            disabled={saving || isEditMode}

            className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-blue-700 active:scale-[0.98] disabled:cursor-not-allowed disabled:bg-blue-300"

          >

            <FaEdit size={14} />

            {isEditMode

              ? "Duke modifikuar..."

              : "Modifiko"}

          </button>

          {/* DELETE */}

          <button

            type="button"

            onClick={() =>

              handleRemove(

                editingProduct.id,

                true

              )

            }

            disabled={saving}

            className="inline-flex items-center justify-center gap-2 rounded-xl border border-red-200 bg-white px-5 py-2.5 text-sm font-bold text-red-600 transition hover:bg-red-50 hover:text-red-700 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"

          >

            <FaTrash size={14} />

            Fshi produktin

          </button>

        </div>

      </div>

    )}

  </div>

  {/* ===================================================

      ERROR MESSAGE

  =================================================== */}

  {modalMessage.text && (

    <div className="mx-6 mt-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">

      {modalMessage.text}

    </div>

  )}

  {/* ===================================================

      READ ONLY VIEW

  =================================================== */}

  {editingProduct && !isEditMode && (

    <div className="max-h-[65vh] overflow-y-auto px-6 py-6">

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">

        {/* NAME */}

        <div className="sm:col-span-2">

          <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">

            <p className="mb-2 text-xs font-bold uppercase tracking-wide text-gray-400">

              Emri i produktit

            </p>

            <p className="text-lg font-bold text-gray-900">

              {editingProduct.name || "—"}

            </p>

          </div>

        </div>

        {/* CATEGORY */}

        <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">

          <p className="mb-2 text-xs font-bold uppercase tracking-wide text-gray-400">

            Kategoria

          </p>

          <p className="font-semibold text-gray-800">

            {editingProduct.category || "—"}

          </p>

        </div>

        {/* ID */}

        <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">

          <p className="mb-2 text-xs font-bold uppercase tracking-wide text-gray-400">

            ID

          </p>

          <p className="font-semibold text-gray-800">

            #{editingProduct.id}

          </p>

        </div>

        {/* PUBLISHED */}

        <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">

          <p className="mb-2 text-xs font-bold uppercase tracking-wide text-gray-400">

            Statusi

          </p>

          <span

            className={`inline-flex rounded-full px-3 py-1.5 text-xs font-bold ${

              Number(

                editingProduct.is_published || 0

              ) === 1

                ? "bg-green-100 text-green-700"

                : "bg-gray-100 text-gray-500"

            }`}

          >

            {Number(

              editingProduct.is_published || 0

            ) === 1

              ? "Publikuar"

              : "Jo publikuar"}

          </span>

        </div>

        {/* OTHER DATA */}

        {Object.entries(editingProduct)

          .filter(

            ([key]) =>

              ![

                "id",

                "name",

                "category",

                "is_published",

              ].includes(key)

          )

          .map(([key, value]) => (

            <div

              key={key}

              className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm"

            >

              <p className="mb-2 text-xs font-bold uppercase tracking-wide text-gray-400">

                {key.replaceAll("_", " ")}

              </p>

              <p className="break-words font-medium text-gray-800">

                {value === null ||

                value === undefined ||

                value === ""

                  ? "—"

                  : String(value)}

              </p>

            </div>

          ))}

      </div>

      {/* CLOSE */}

      <div className="mt-6 flex justify-end border-t border-gray-200 pt-5">

        <button

          type="button"

          onClick={closeDialog}

          className="rounded-xl bg-gray-100 px-6 py-2.5 text-sm font-bold text-gray-700 transition hover:bg-gray-200"

        >

          Mbyll

        </button>

      </div>

    </div>

  )}

  {/* ===================================================

      EDIT / ADD FORM

  =================================================== */}

  {isEditMode && (

    <div className="max-h-[70vh] overflow-y-auto">

      <div className="px-6 py-6">

        <AddProductForm

          ref={formRef}

          initialData={editingProduct}

          products={products}

          onAdd={handleSaveProduct}

          onError={(errorMessage) =>

            setModalMessage({

              type: "error",

              text:

                errorMessage ||

                "Kontrollo të dhënat e produktit.",

            })

          }

          onClose={closeDialog}

        />

      </div>

    </div>

  )}

</dialog>

      </main>

    </div>

  );

};

export default AdminProducts;

const productsDesign = `

/* Produkte — pamje sipas Furnitorëve. Të gjitha rregullat janë të kufizuara te kjo faqe. */

.mp-products{background:#f9fafb;color:#111827;font-family:inherit}.mp-products *{box-sizing:border-box}

.mp-products main{padding-top:32px;padding-bottom:40px}.mp-products .mp-title{background:transparent;padding:0;margin-bottom:24px;border:0;box-shadow:none}.mp-title h1{font-size:30px;font-weight:700;line-height:1.25;margin-top:0}.mp-title p{font-size:14px;color:#6b7280}.mp-title>button{border-radius:12px;background:#2563eb;box-shadow:none;font-size:14px;padding:12px 20px}.mp-title>button:hover{background:#1d4ed8}

.mp-stats{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:20px;margin-bottom:24px}.mp-stat{padding:22px 24px;background:white;border:1px solid #e5e7eb;border-radius:16px;box-shadow:0 1px 2px #00000003}.mp-stat:first-child{border-color:#93c5fd}.mp-stat p{font-size:14px;color:#4b5563;margin:0 0 12px}.mp-stat strong{display:inline-flex;min-width:50px;padding:6px 12px;font-size:25px;font-weight:700;line-height:1.2;border-radius:10px}.mp-stat small{display:block;font-size:12px;color:#9ca3af;margin-top:12px}.mp-blue strong{color:#2563eb;background:#eff6ff}.mp-purple strong{color:#9333ea;background:#faf5ff}.mp-green strong{color:#059669;background:#ecfdf5}.mp-red strong{color:#dc2626;background:#fef2f2}

.mp-products .mp-filters{padding:0;border:1px solid #e5e7eb;border-radius:16px;box-shadow:none;margin-bottom:24px}.mp-filter-heading{display:flex;justify-content:space-between;align-items:center;gap:16px;padding:22px 24px;border-bottom:1px solid #e5e7eb}.mp-filter-heading h2{font-size:18px;font-weight:700;color:#111827;margin:0 0 5px}.mp-filter-heading p{font-size:14px;color:#6b7280;margin:0}.mp-filter-heading>span{padding:6px 12px;border:1px solid #e5e7eb;border-radius:999px;color:#4b5563;background:#f9fafb;font-size:12px;white-space:nowrap}.mp-filters>.grid{padding:20px 24px}.mp-filters input,.mp-filters select{font-size:14px;border-color:#d1d5db;border-radius:10px}.mp-filters button{font-size:14px;font-weight:600;border-radius:10px}.mp-products .mp-results{display:none}

.mp-products .mp-table-panel{border-color:#e5e7eb;box-shadow:none;border-radius:16px}.mp-table-panel table{font-size:14px}.mp-table-panel thead{background:#f9fafb;color:#6b7280;border-bottom:1px solid #e5e7eb}.mp-table-panel th{font-size:12px;text-transform:uppercase;letter-spacing:.4px;font-weight:600;padding:16px 24px}.mp-table-panel td{padding:18px 24px;border-color:#f3f4f6}.mp-table-panel tbody tr:hover{background:#f9fafb}.mp-table-panel td:nth-child(2) button{color:#111827;font-size:14px;font-weight:600;text-decoration:none}.mp-table-panel td:nth-child(2) button:hover{color:#2563eb}.mp-table-panel td:nth-child(4) span{font-size:12px;font-weight:600;padding:6px 12px}.mp-table-panel td:last-child button{border:1px solid #e5e7eb;border-radius:8px}

.mp-pagination{margin-top:24px;gap:10px}.mp-pagination button{width:42px;height:42px;padding:0;background:white;border:1px solid #e5e7eb;border-radius:9px;box-shadow:none}.mp-pagination>span{display:flex;align-items:center;justify-content:center;min-width:90px;height:42px;border:1px solid #e5e7eb;border-radius:9px;box-shadow:none;font-size:14px;font-weight:500}

.mp-product-dialog{border:1px solid #e5e7eb;border-radius:16px;color:#111827}.mp-product-dialog::backdrop{background:#15243a88;backdrop-filter:blur(4px)}.mp-product-dialog h2{font-size:22px}.mp-product-dialog .shadow-sm{box-shadow:none}.mp-product-dialog .shadow-2xl{box-shadow:0 30px 100px #182c5040}

@media(max-width:1023px){.mp-products main{padding-top:80px}.mp-stats{grid-template-columns:repeat(2,minmax(0,1fr));gap:14px}}

@media(max-width:640px){.mp-title h1{font-size:26px}.mp-stats{gap:10px}.mp-stat{padding:16px}.mp-stat p{font-size:12px}.mp-stat strong{font-size:24px}.mp-filter-heading{padding:18px 16px;align-items:flex-start}.mp-filter-heading h2{font-size:16px}.mp-filter-heading p{font-size:12px}.mp-filters>.grid{padding:16px}.mp-table-panel th,.mp-table-panel td{padding:14px 16px}}

`;